import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  as?: 'div' | 'article' | 'section';
}

const variantStyles = {
  default: `
    bg-white dark:bg-surface-800
    border border-surface-200 dark:border-surface-700
    shadow-card
  `,
  elevated: `
    bg-white dark:bg-surface-800
    shadow-soft
    border border-surface-100 dark:border-surface-700
  `,
  outlined: `
    bg-transparent
    border-2 border-surface-200 dark:border-surface-700
  `,
  filled: `
    bg-surface-100 dark:bg-surface-800
    border border-transparent
  `,
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hoverable = false,
      as: Component = 'div',
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <Component
        ref={ref}
        className={`
          rounded-xl
          transition-all duration-200 ease-out
          ${variantStyles[variant]}
          ${paddingStyles[padding]}
          ${hoverable ? 'hover:shadow-lg hover:border-surface-300 dark:hover:border-surface-600 cursor-pointer' : ''}
          ${className}
        `}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// Card subcomponents for structured content
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
}

export function CardHeader({
  title,
  subtitle,
  action,
  children,
  className = '',
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={`flex items-start justify-between gap-4 mb-4 ${className}`}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 truncate">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
            {subtitle}
          </p>
        )}
        {children}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function CardContent({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`text-surface-700 dark:text-surface-300 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`
        flex items-center gap-3 mt-6 pt-4
        border-t border-surface-200 dark:border-surface-700
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
}

export default Card;
